import Tooltip from "@/components/atoms/Tooltip/Tooltip";
import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";
import {
  SBC_SOLUTION_TAGS,
  SbcSolutionTag,
  StreamlineTag,
} from "@repo/shared/types";

/** A strategy behind a squad: a Streamline solver tag, or a normal solve's pool. */
export type SolutionStrategyTag = StreamlineTag | SbcSolutionTag;
export type SolutionTagTone = SolutionStrategyTag | "recommended";

/* Strategies on the neutral pill with only their text coloured (tokens in design-system.css);
   Recommended keeps its tier fill. */
const TONE: Record<SolutionTagTone, string> = {
  fastest: "text-solution-fast bg-neutral-800 border-neutral-700",
  lmv: "text-solution-cheap bg-neutral-800 border-neutral-700",
  cheapest: "text-solution-low-market bg-neutral-800 border-neutral-700",
  clubOnly: "text-solution-club bg-neutral-800 border-neutral-700",
  club: "text-solution-club bg-neutral-800 border-neutral-700",
  both: "text-solution-both bg-neutral-800 border-neutral-700",
  market: "text-solution-cheap bg-neutral-800 border-neutral-700",
  recommended:
    "text-solution-recommended/80 bg-s-tier-background/60 border-s-tier-border/50",
};

const isSbcSolutionTag = (tag: SolutionStrategyTag): tag is SbcSolutionTag =>
  (SBC_SOLUTION_TAGS as string[]).includes(tag);

/** NewTag's shape, naming a strategy; the explanation sits in a tooltip. */
export const SolutionTag = ({
  label,
  explanation,
  tone,
}: {
  label: string;
  explanation: string;
  tone: SolutionTagTone;
}) => (
  <Tooltip content={explanation} delayDuration={150} className="z-[120]">
    <span
      className={cn(
        "flex items-center justify-center rounded-3xl text-xs px-2 h-5 w-fit whitespace-nowrap border",
        TONE[tone],
      )}
    >
      <p className="leading-0">{label}</p>
    </span>
  </Tooltip>
);

/** A strategy pill with its copy looked up by tag: Streamline tags and solve pools have their own `sbcs` blocks. */
export const StrategyTag = ({ tag }: { tag: SolutionStrategyTag }) => {
  const { t } = useTranslation("sbcs");
  return isSbcSolutionTag(tag) ? (
    <SolutionTag
      tone={tag}
      label={t(`solutions.tags.${tag}.title`)}
      explanation={t(`solutions.tags.${tag}.explanation`)}
    />
  ) : (
    <SolutionTag
      tone={tag}
      label={t(`streamline.tags.${tag}.title`)}
      explanation={t(`streamline.tags.${tag}.explanation`)}
    />
  );
};

export const RecommendedTag = () => {
  const { t } = useTranslation("sbcs");
  return (
    <SolutionTag
      tone="recommended"
      label={t("streamline.recommended")}
      explanation={t("streamline.recommendedExplanation")}
    />
  );
};
